import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CheckSquare,
  Award,
  UserCircle,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { lastRouteStore } from "@/lib/api";
import { TID } from "@/testIds";

const buildItems = (isAdmin) =>
  [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: TID.navDashboard },
    { to: "/students", label: "Students", icon: Users, testId: TID.navStudents },
    { to: "/attendance", label: "Attendance", icon: CheckSquare, testId: TID.navAttendance },
    isAdmin && { to: "/coaches", label: "Coaches", icon: Award, testId: TID.navCoaches },
    { to: "/profile", label: "Profile", icon: UserCircle, testId: TID.navProfile },
  ].filter(Boolean);

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = buildItems(user?.role === "admin");

  const isFirstMount = useRef(true);
  useEffect(() => {
    const current = location.pathname + location.search;
    if (isFirstMount.current) {
      isFirstMount.current = false;
      // Only override a "no specific intent" landing — bare root, or
      // /dashboard itself, since iOS "Add to Home Screen" pins the
      // shortcut to whatever exact URL was on screen when it was created
      // (commonly /dashboard), which otherwise bypasses this entirely. A
      // reload or deep link to any other page (e.g. /attendance,
      // /coaches/:id/edit) must keep showing that exact page, so this
      // never runs for them.
      if (location.pathname === "/" || location.pathname === "/dashboard") {
        const saved = lastRouteStore.get();
        const target = saved && saved !== "/" ? saved : "/dashboard";
        if (target !== location.pathname) {
          navigate(target, { replace: true });
          return;
        }
      }
    }
    lastRouteStore.save(current);
  }, [location.pathname, location.search, navigate]);

  const onLogout = () => {
    lastRouteStore.clear();
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Desktop sidebar */}
      <aside
        data-testid={TID.sidebar}
        className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-slate-200 bg-white z-40"
      >
        <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0A2540] flex items-center justify-center">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-bold text-slate-900 leading-tight">Pathaans</div>
            <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
              Academy · Ludhiana
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-[#0A2540] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2 truncate">
            <span className="font-semibold text-slate-700">{user?.full_name}</span>
            <span className="ml-2 uppercase tracking-wider text-[10px] font-bold text-[#FF6B6B]">
              {user?.role}
            </span>
          </div>
          <button
            onClick={onLogout}
            data-testid={TID.logoutButton}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0A2540] flex items-center justify-center">
            <Award className="w-4 h-4 text-white" />
          </div>
          <div className="font-bold text-sm">Pathaans</div>
        </div>
        <button
          onClick={onLogout}
          data-testid={`${TID.logoutButton}-mobile`}
          className="text-slate-500 hover:text-slate-900"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 grid grid-cols-5">
        {items.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`${testId}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold ${
                isActive ? "text-[#0A2540]" : "text-slate-500"
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      <main className="md:pl-64 pb-24 md:pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
